import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Collection Threads RPC Contract
 *
 * Tracks collections follow-up items (short pays, credit limit expiries, etc.)
 * with a per-thread activity feed of comments and system events.
 */

/**
 * Human-readable thread identifier, e.g. `COL-1024`. The numeric part is a
 * global auto-increment column (`referenceNumber`) — see the model. Keep the
 * prefix and (un)formatting here so the backend serializer, the quick-find
 * search, and the frontend all derive the code the same way.
 */
export const COLLECTION_THREAD_CODE_PREFIX = "COL-";

export function formatCollectionThreadCode(referenceNumber: number): string {
  return `${COLLECTION_THREAD_CODE_PREFIX}${referenceNumber}`;
}

/**
 * Pulls the numeric reference out of a user-typed code. Tolerates the prefix
 * in any case ("col-12", "COL-12") and a bare number ("12"). Returns null when
 * no positive integer is present so callers can fall back to title matching.
 */
export function parseCollectionThreadCode(input: string): number | null {
  const digits = input.replace(/[^0-9]/g, "");
  if (digits.length === 0) return null;
  const parsed = Number.parseInt(digits, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export const CollectionThreadTypeSchema = z.enum([
  "short_pay",
  "over_payment",
  "deduction",
  "other",
]);

export const CollectionThreadStatusSchema = z.enum(["open", "resolved"]);

export const CollectionThreadResolutionSchema = z.enum([
  "paid_in_full",
  "short_pay_accepted",
  "written_off",
  "customer_no_response",
  "no_longer_relevant",
  // Over-payment outcomes — the residual is money owed back to the customer.
  "refunded",
  "applied_to_account",
  "credit_issued",
  // Deduction outcomes — set after an AR rep validates the claimed deduction.
  "deduction_approved",
  "deduction_charged_back",
  "other",
]);

export const CollectionThreadActivityKindSchema = z.enum([
  "comment",
  "status_change",
  "assignment_change",
  "system_event",
]);

export const CollectionThreadActivitySchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  kind: CollectionThreadActivityKindSchema,
  body: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: z.string().uuid().nullable(),
  /**
   * Display name of the user who left the activity. Null when the activity
   * was system-generated (no actor) or the user record is unavailable.
   */
  actorName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CollectionThreadSchema = z.object({
  id: z.string().uuid(),
  /** Global auto-increment sequence behind the readable code. */
  referenceNumber: z.number().int(),
  /** Human-readable identifier derived from `referenceNumber`, e.g. `COL-1024`. */
  referenceCode: z.string(),
  customerId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  type: CollectionThreadTypeSchema,
  status: CollectionThreadStatusSchema,
  resolution: CollectionThreadResolutionSchema.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  assigneeUserId: z.string().uuid().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  resolvedByUserId: z.string().uuid().nullable(),
  resolvedAt: z.string().nullable(),
  /**
   * Snooze gate. When set to a future timestamp the thread is suppressed from
   * reminders and sinks to the bottom of the urgency ranking until it elapses.
   * Null means not snoozed. Also bumped by the reminder workflow's auto-cooldown.
   */
  remindAfter: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CollectionThreadWithActivitiesSchema =
  CollectionThreadSchema.extend({
    activities: z.array(CollectionThreadActivitySchema),
  });

/**
 * Sortable columns for the cross-customer threads table. Each maps to a
 * concrete order clause in the service — `customerName` orders by the joined
 * customer record. Mirrors the `sortBy`/`sortDirection` convention used by the
 * AR invoices and payments list tables.
 */
export const CollectionThreadSortBySchema = z.enum([
  "referenceNumber",
  "customerName",
  "title",
  "type",
  "status",
  "createdAt",
  "resolvedAt",
  // Composite urgency (type severity + dollars at risk + staleness), with
  // snoozed and resolved threads pushed to the bottom. Not a column — ranked
  // in memory by the service. Powers the Threads-tab "Sort by urgency" toggle.
  "urgency",
]);

export const ListCollectionThreadsInputSchema = z.object({
  customerId: z.string().uuid().optional(),
  invoiceId: z.string().uuid().optional(),
  assigneeUserId: z.string().uuid().optional(),
  status: z.array(CollectionThreadStatusSchema).optional(),
  type: z.array(CollectionThreadTypeSchema).optional(),
  resolution: z.array(CollectionThreadResolutionSchema).optional(),
  /** Free-text match on title, the `COL-` code, or customer name. */
  search: z.string().optional(),
  sortBy: CollectionThreadSortBySchema.optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  offset: z.number().int().min(0).optional(),
});

/**
 * A thread enriched with its customer name, for the cross-customer list table
 * (the base `CollectionThreadSchema` carries only `customerId`).
 */
export const CollectionThreadListItemSchema = CollectionThreadSchema.extend({
  customerName: z.string().nullable(),
});

export const ListCollectionThreadsOutputSchema = z.object({
  threads: z.array(CollectionThreadListItemSchema),
  total: z.number().int(),
});

/**
 * A thread enriched with the signals used to rank "needs attention":
 * - `dollarsAtRisk`: linked invoice balance, falling back to the customer's
 *   overdue balance when no invoice is linked (decimal string).
 * - `daysSinceLastActivity`: whole days since the most recent activity row;
 *   null when the thread has no activity yet.
 * - `urgencyScore`: composite of type severity + dollars + staleness. Derived
 *   at query time; never persisted.
 */
export const RankedCollectionThreadSchema = CollectionThreadSchema.extend({
  customerName: z.string().nullable(),
  dollarsAtRisk: z.string(),
  daysSinceLastActivity: z.number().nullable(),
  urgencyScore: z.number(),
});

export const ListRankedCollectionThreadsInputSchema = z.object({
  limit: z.number().int().min(1).max(200).optional(),
  type: z.array(CollectionThreadTypeSchema).optional(),
  assigneeUserId: z.string().uuid().optional(),
});

export const ListRankedCollectionThreadsOutputSchema = z.object({
  threads: z.array(RankedCollectionThreadSchema),
});

export const GetCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * One row per customer with open collection threads — powers the Collections
 * inbox left rail and the AR Summary mini "customers needing attention" card.
 *
 * - `openCount`: number of OPEN threads on this customer (resolved threads
 *   excluded so the rail reflects the active queue).
 * - `totalAtRisk`: sum of linked invoice balances for the open threads
 *   (decimal string). Threads without a linked invoice contribute 0.
 * - `oldestOpenedAt`: timestamp of the oldest still-open thread on this
 *   customer; primary sort key so reps see the staleness leader first.
 * - `mostRecentActivityAt`: latest `updatedAt` across the customer's open
 *   threads; surfaces "something happened recently" without a separate query.
 */
export const InboxCustomerSummarySchema = z.object({
  customerId: z.string().uuid(),
  customerName: z.string().nullable(),
  openCount: z.number().int(),
  totalAtRisk: z.string(),
  oldestOpenedAt: z.string(),
  mostRecentActivityAt: z.string(),
});

export const ListInboxCustomersInputSchema = z.object({
  limit: z.number().int().min(1).max(500).optional(),
});

export const ListInboxCustomersOutputSchema = z.object({
  customers: z.array(InboxCustomerSummarySchema),
});

export const CreateCollectionThreadInputSchema = z.object({
  customerId: z.string().uuid(),
  invoiceId: z.string().uuid().optional(),
  type: CollectionThreadTypeSchema,
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  assigneeUserId: z.string().uuid().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const UpdateCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  // Pass null to clear the linked invoice. Setting a different invoice
  // re-derives evidence on the backend (old evidence rows are dropped).
  invoiceId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const ResolveCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  resolution: CollectionThreadResolutionSchema,
  comment: z.string().optional(),
});

export const ReopenCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  comment: z.string().optional(),
});

/**
 * Snooze a thread by setting its reminder gate. `remindAfter` is an absolute
 * "snooze until" timestamp; while it is in the future the thread is excluded
 * from reminders and sinks below all active threads in the urgency ranking.
 * Pass `null` to clear the snooze immediately.
 */
export const SnoozeCollectionThreadInputSchema = z.object({
  id: z.string().uuid(),
  remindAfter: z.string().datetime({ offset: true }).nullable(),
});

export const AddCollectionThreadCommentInputSchema = z.object({
  id: z.string().uuid(),
  body: z.string().min(1),
});

// --- Evidence DTOs ---
// Lightweight projections of related entities surfaced on the thread detail
// view. These mirror the relevant subset of fields from the underlying
// models — enough to render a card and link out, no more.

export const CollectionThreadEvidencePaymentApplicationSchema = z.object({
  id: z.string().uuid(),
  paymentId: z.string().uuid(),
  invoiceId: z.string().uuid(),
  appliedAmount: z.string(),
  applicationDate: z.string().nullable(),
  sequenceNumber: z.number().int().nullable(),
});

export const CollectionThreadEvidencePaymentSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  paymentDate: z.string().nullable(),
  paymentAmount: z.string(),
  paymentMethod: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  status: z.string(),
});

export const CollectionThreadEvidenceRemittanceAdviceSchema = z.object({
  id: z.string().uuid(),
  paymentId: z.string().uuid().nullable(),
  documentId: z.string().uuid().nullable(),
  inboundEmailId: z.string().uuid().nullable(),
  channel: z.string(),
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: z.string().nullable(),
  statedPaymentDate: z.string().nullable(),
  statedPaymentMethod: z.string().nullable(),
  statedReferenceNo: z.string().nullable(),
});

export const CollectionThreadEvidenceRemittanceLineItemSchema = z.object({
  id: z.string().uuid(),
  remittanceAdviceId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  rawInvoiceRef: z.string(),
  statedAmount: z.string().nullable(),
  matchStatus: z.string(),
  matchReason: z.string().nullable(),
});

export const CollectionThreadEvidenceInboundEmailSchema = z.object({
  id: z.string().uuid(),
  from: z.string().nullable(),
  to: z.string(),
  subject: z.string().nullable(),
  service: z.string(),
  receivedAt: z.string(),
  /** Classification types of the documents this email delivered (e.g. POD/BOL). */
  carriedDocumentClassifications: z.array(z.string()),
});

export const CollectionThreadEvidenceDocumentSchema = z.object({
  id: z.string().uuid(),
  originalFileName: z.string().nullable(),
  status: z.string(),
  classificationType: z.string().nullable(),
  storagePath: z.string().nullable(),
  /** Bytes on disk. Null when the upload pipeline never recorded a size. */
  fileSizeBytes: z.number().nullable(),
  createdAt: z.string(),
});

export const CollectionThreadEvidenceShipmentSchema = z.object({
  id: z.string().uuid(),
  salesOrderId: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  status: z.string(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  carrier: z.string().nullable(),
  trackingReference: z.string().nullable(),
});

export const CollectionThreadEvidenceInvoiceSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  invoiceNumber: z.string().nullable(),
  status: z.string(),
  invoiceDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  grossAmount: z.string().nullable(),
  balanceDue: z.string().nullable(),
  currencyCode: z.string().nullable(),
});

export const CollectionThreadEvidenceSalesOrderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  status: z.string(),
  orderDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
  customerPurchaseOrderNumber: z.string().nullable(),
});

export const CollectionThreadEvidenceCustomerPurchaseOrderSchema = z.object({
  id: z.string().uuid(),
  internalId: z.number(),
  purchaseOrderNumber: z.string(),
  status: z.string(),
  orderDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
});

export const CollectionThreadEvidenceApInvoiceSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  status: z.string(),
  paymentStatus: z.string(),
  invoiceDate: z.string().nullable(),
  totalAmount: z.string().nullable(),
  currency: z.string(),
  /**
   * True when a user linked this invoice via the manual "Link AP invoice"
   * action (vs. derived from supporting documents). The UI shows "Unlink" only
   * for these — document-derived invoices aren't directly removable.
   */
  manuallyLinked: z.boolean(),
});

export const ListCollectionThreadEvidenceInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * Files a previously-uploaded document as evidence on a thread. The document
 * must already be attached to the thread via the standard upload flow
 * (entityType `ar_collection_thread`); this links it into the thread's evidence
 * graph (COLLECTION_THREAD_EVIDENCE) and records a timeline activity.
 */
export const FileCollectionThreadDocumentEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  documentId: z.string().uuid(),
});

export const FileCollectionThreadDocumentEvidenceOutputSchema = z.object({
  /** False when the document was already filed on this thread (no-op). */
  filed: z.boolean(),
});

/**
 * Manually links an existing AP (vendor/carrier) invoice as evidence on a
 * thread. Links the invoice itself plus its supporting documents (BOL, POD,
 * carrier confirmation) into the evidence graph and records a timeline
 * activity. Use when the automatic gather missed it (e.g. a freight invoice
 * matched only by a shared reference number, with no AP_INVOICE_MATCHING row).
 */
export const LinkCollectionThreadApInvoiceEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  apInvoiceId: z.string().uuid(),
});

export const LinkCollectionThreadApInvoiceEvidenceOutputSchema = z.object({
  /** False when the invoice was already linked to this thread (no-op). */
  linked: z.boolean(),
  /** Supporting documents attached alongside the invoice. */
  linkedDocumentCount: z.number().int(),
});

/**
 * Removes a manually-linked AP invoice (and the supporting documents the manual
 * link brought along) from a thread's evidence. Only affects manually-filed
 * associations — gather-derived, email-filed, and upload-filed evidence is
 * never touched.
 */
export const UnlinkCollectionThreadApInvoiceEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
  apInvoiceId: z.string().uuid(),
});

export const UnlinkCollectionThreadApInvoiceEvidenceOutputSchema = z.object({
  /** False when there was no manual link to remove (no-op). */
  unlinked: z.boolean(),
});

/**
 * Re-runs the evidence gather for a thread (the same walk that fires on thread
 * creation). Idempotent and re-runnable — picks up evidence that landed after
 * the thread was opened, including AP invoices newly matched by reference_ids.
 */
export const RefreshCollectionThreadEvidenceInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

export const RefreshCollectionThreadEvidenceOutputSchema = z.object({
  /** Evidence rows newly linked on this run. */
  linked: z.number().int(),
  /** Rows skipped (already linked, or target since deleted). */
  skipped: z.number().int(),
  /** Rows that failed to link (unexpected errors). */
  failed: z.number().int(),
});

export const ListCollectionThreadEvidenceOutputSchema = z.object({
  paymentApplications: z.array(
    CollectionThreadEvidencePaymentApplicationSchema,
  ),
  payments: z.array(CollectionThreadEvidencePaymentSchema),
  remittanceAdvices: z.array(CollectionThreadEvidenceRemittanceAdviceSchema),
  remittanceLineItems: z.array(
    CollectionThreadEvidenceRemittanceLineItemSchema,
  ),
  inboundEmails: z.array(CollectionThreadEvidenceInboundEmailSchema),
  documents: z.array(CollectionThreadEvidenceDocumentSchema),
  shipments: z.array(CollectionThreadEvidenceShipmentSchema),
  invoice: CollectionThreadEvidenceInvoiceSchema.nullable(),
  salesOrder: CollectionThreadEvidenceSalesOrderSchema.nullable(),
  customerPurchaseOrder:
    CollectionThreadEvidenceCustomerPurchaseOrderSchema.nullable(),
  apInvoices: z.array(CollectionThreadEvidenceApInvoiceSchema),
});

/**
 * Generates (or reads from cache) short, human-readable names for the thread's
 * evidence documents. Separate from listEvidence so the evidence list renders
 * immediately while names — which may require a cheap LLM call on a cold cache —
 * resolve in a second request.
 */
export const ListCollectionThreadDocumentNamesInputSchema = z.object({
  /** Thread id. */
  id: z.string().uuid(),
});

export const ListCollectionThreadDocumentNamesOutputSchema = z.object({
  /** Only documents that resolved to a name; the UI falls back for the rest. */
  names: z.array(
    z.object({
      documentId: z.string().uuid(),
      displayName: z.string().min(1),
    }),
  ),
});

export const arCollectionThreadsContracts = {
  list: oc
    .input(ListCollectionThreadsInputSchema)
    .output(ListCollectionThreadsOutputSchema)
    .route({ method: "GET" }),
  listRanked: oc
    .input(ListRankedCollectionThreadsInputSchema)
    .output(ListRankedCollectionThreadsOutputSchema)
    .route({ method: "GET" }),
  listInboxCustomers: oc
    .input(ListInboxCustomersInputSchema)
    .output(ListInboxCustomersOutputSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "POST" }),
  resolve: oc
    .input(ResolveCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "POST" }),
  reopen: oc
    .input(ReopenCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "POST" }),
  snooze: oc
    .input(SnoozeCollectionThreadInputSchema)
    .output(CollectionThreadWithActivitiesSchema)
    .route({ method: "POST" }),
  addComment: oc
    .input(AddCollectionThreadCommentInputSchema)
    .output(CollectionThreadActivitySchema)
    .route({ method: "POST" }),
  listEvidence: oc
    .input(ListCollectionThreadEvidenceInputSchema)
    .output(ListCollectionThreadEvidenceOutputSchema)
    .route({ method: "GET" }),
  fileDocumentEvidence: oc
    .input(FileCollectionThreadDocumentEvidenceInputSchema)
    .output(FileCollectionThreadDocumentEvidenceOutputSchema)
    .route({ method: "POST" }),
  linkApInvoiceEvidence: oc
    .input(LinkCollectionThreadApInvoiceEvidenceInputSchema)
    .output(LinkCollectionThreadApInvoiceEvidenceOutputSchema)
    .route({ method: "POST" }),
  unlinkApInvoiceEvidence: oc
    .input(UnlinkCollectionThreadApInvoiceEvidenceInputSchema)
    .output(UnlinkCollectionThreadApInvoiceEvidenceOutputSchema)
    .route({ method: "POST" }),
  refreshEvidence: oc
    .input(RefreshCollectionThreadEvidenceInputSchema)
    .output(RefreshCollectionThreadEvidenceOutputSchema)
    .route({ method: "POST" }),
  documentNames: oc
    .input(ListCollectionThreadDocumentNamesInputSchema)
    .output(ListCollectionThreadDocumentNamesOutputSchema)
    .route({ method: "GET" }),
} as const;

export type CollectionThreadType = z.infer<typeof CollectionThreadTypeSchema>;
export type CollectionThreadStatus = z.infer<
  typeof CollectionThreadStatusSchema
>;
export type CollectionThreadResolution = z.infer<
  typeof CollectionThreadResolutionSchema
>;
export type CollectionThreadActivityKind = z.infer<
  typeof CollectionThreadActivityKindSchema
>;
export type CollectionThreadDto = z.infer<typeof CollectionThreadSchema>;
export type CollectionThreadActivityDto = z.infer<
  typeof CollectionThreadActivitySchema
>;
export type CollectionThreadWithActivitiesDto = z.infer<
  typeof CollectionThreadWithActivitiesSchema
>;
export type CollectionThreadSortBy = z.infer<
  typeof CollectionThreadSortBySchema
>;
export type CollectionThreadListItemDto = z.infer<
  typeof CollectionThreadListItemSchema
>;
export type ListCollectionThreadsInput = z.infer<
  typeof ListCollectionThreadsInputSchema
>;
export type RankedCollectionThreadDto = z.infer<
  typeof RankedCollectionThreadSchema
>;
export type ListRankedCollectionThreadsInput = z.infer<
  typeof ListRankedCollectionThreadsInputSchema
>;
export type ListRankedCollectionThreadsOutput = z.infer<
  typeof ListRankedCollectionThreadsOutputSchema
>;
export type InboxCustomerSummaryDto = z.infer<
  typeof InboxCustomerSummarySchema
>;
export type ListInboxCustomersInput = z.infer<
  typeof ListInboxCustomersInputSchema
>;
export type ListInboxCustomersOutput = z.infer<
  typeof ListInboxCustomersOutputSchema
>;
export type GetCollectionThreadInput = z.infer<
  typeof GetCollectionThreadInputSchema
>;
export type CreateCollectionThreadInput = z.infer<
  typeof CreateCollectionThreadInputSchema
>;
export type UpdateCollectionThreadInput = z.infer<
  typeof UpdateCollectionThreadInputSchema
>;
export type ResolveCollectionThreadInput = z.infer<
  typeof ResolveCollectionThreadInputSchema
>;
export type ReopenCollectionThreadInput = z.infer<
  typeof ReopenCollectionThreadInputSchema
>;
export type SnoozeCollectionThreadInput = z.infer<
  typeof SnoozeCollectionThreadInputSchema
>;
export type AddCollectionThreadCommentInput = z.infer<
  typeof AddCollectionThreadCommentInputSchema
>;
export type CollectionThreadEvidencePaymentApplicationDto = z.infer<
  typeof CollectionThreadEvidencePaymentApplicationSchema
>;
export type CollectionThreadEvidencePaymentDto = z.infer<
  typeof CollectionThreadEvidencePaymentSchema
>;
export type CollectionThreadEvidenceRemittanceAdviceDto = z.infer<
  typeof CollectionThreadEvidenceRemittanceAdviceSchema
>;
export type CollectionThreadEvidenceInboundEmailDto = z.infer<
  typeof CollectionThreadEvidenceInboundEmailSchema
>;
export type CollectionThreadEvidenceDocumentDto = z.infer<
  typeof CollectionThreadEvidenceDocumentSchema
>;
export type CollectionThreadEvidenceShipmentDto = z.infer<
  typeof CollectionThreadEvidenceShipmentSchema
>;
export type CollectionThreadEvidenceInvoiceDto = z.infer<
  typeof CollectionThreadEvidenceInvoiceSchema
>;
export type CollectionThreadEvidenceSalesOrderDto = z.infer<
  typeof CollectionThreadEvidenceSalesOrderSchema
>;
export type CollectionThreadEvidenceCustomerPurchaseOrderDto = z.infer<
  typeof CollectionThreadEvidenceCustomerPurchaseOrderSchema
>;
export type CollectionThreadEvidenceApInvoiceDto = z.infer<
  typeof CollectionThreadEvidenceApInvoiceSchema
>;
export type ListCollectionThreadEvidenceInput = z.infer<
  typeof ListCollectionThreadEvidenceInputSchema
>;
export type ListCollectionThreadEvidenceOutput = z.infer<
  typeof ListCollectionThreadEvidenceOutputSchema
>;
export type ListCollectionThreadDocumentNamesInput = z.infer<
  typeof ListCollectionThreadDocumentNamesInputSchema
>;
export type ListCollectionThreadDocumentNamesOutput = z.infer<
  typeof ListCollectionThreadDocumentNamesOutputSchema
>;
export type FileCollectionThreadDocumentEvidenceInput = z.infer<
  typeof FileCollectionThreadDocumentEvidenceInputSchema
>;
export type LinkCollectionThreadApInvoiceEvidenceInput = z.infer<
  typeof LinkCollectionThreadApInvoiceEvidenceInputSchema
>;
export type LinkCollectionThreadApInvoiceEvidenceOutput = z.infer<
  typeof LinkCollectionThreadApInvoiceEvidenceOutputSchema
>;
export type UnlinkCollectionThreadApInvoiceEvidenceInput = z.infer<
  typeof UnlinkCollectionThreadApInvoiceEvidenceInputSchema
>;
export type UnlinkCollectionThreadApInvoiceEvidenceOutput = z.infer<
  typeof UnlinkCollectionThreadApInvoiceEvidenceOutputSchema
>;
export type RefreshCollectionThreadEvidenceInput = z.infer<
  typeof RefreshCollectionThreadEvidenceInputSchema
>;
export type RefreshCollectionThreadEvidenceOutput = z.infer<
  typeof RefreshCollectionThreadEvidenceOutputSchema
>;
